import { rank, route, type Finding } from '@/shared/ui/council'
import type { DomainCard, Platform } from './model'

export type { Finding }

const num = (n: number | null | undefined) =>
  n == null ? '—' : Math.round(n).toLocaleString()
const pct = (n: number | null | undefined) =>
  n == null ? '—' : `${Math.round(n * 100)}%`

/**
 * The platform council — every module's worst problem, in one queue.
 *
 * ## Why a cross-platform council is a different thing from six of them
 *
 * Each module's council ranks its own findings against its own stake, which is right inside
 * that module and useless across them: two thousand engagements, forty incidents and three
 * hundred votes are not comparable quantities, and a naive merge would sort the loudest
 * unit to the top rather than the most urgent problem.
 *
 * So this ranks by TIME PRESSURE first — what gets worse while nobody looks at it — and
 * only then by size. A silent turnout cell at two in the afternoon outranks an unreconciled
 * ledger, not because it is bigger but because the ledger will still be there tomorrow and
 * the afternoon will not.
 *
 * ## What it will not do
 *
 * It never invents a cross-module causal link. "Incidents are up in the wards where turnout
 * is behind" is a correlation this page can see and a claim it cannot support, and offering
 * it as a finding would put a campaign on a course nothing here justifies. Where two domains
 * are worth reading together the council says so as a QUESTION, and points at both screens.
 */
export function advise(p: Platform, cards: DomainCard[]): Finding[] {
  const out: Finding[] = []

  // ── the afternoon, if there is one ────────────────────────────────────────
  const f = p.floor
  if (f?.totals && f.day.pollsOpen) {
    const t = f.totals
    if (t.cellsStalled > 0) {
      out.push({
        id: 'turnout-silent',
        severity: 'critical',
        headline: `${num(t.cellsStalled)} turnout ${t.cellsStalled === 1 ? 'cell has' : 'cells have'} gone quiet on polling day.`,
        evidence: `${num(t.voted)} of ${num(t.members)} members voted; ${num(t.outstanding)} `
          + `outstanding with ${f.day.minutesToClose} minutes to close.`,
        action: 'Ring those coordinators before moving any vehicle. Nothing else on this page gets worse in the next hour; this does, and it stops mattering entirely once the polls shut.',
        votesAtStake: t.outstanding,
      })
    }
    const behind = Math.round(t.members * f.day.elapsedShare) - t.voted
    if (behind > t.members * 0.1) {
      out.push({
        id: 'turnout-pace',
        severity: 'critical',
        headline: `Turnout is ${num(behind)} votes behind the clock.`,
        evidence: `${pct(t.members > 0 ? t.voted / t.members : 0)} of members have voted against `
          + `${pct(f.day.elapsedShare)} of the polling window gone. ${num(t.transportGaps)} unmatched lifts.`,
        action: 'This is the only number on the platform that cannot be recovered tomorrow. Work the cells furthest behind, not the ones easiest to reach.',
        votesAtStake: behind,
      })
    }
  }

  // ── social, while it is spreading ─────────────────────────────────────────
  const g = p.graph
  if (g) {
    const unanswered = g.threads.filter(
      (x) => x.threat != null && x.threat >= g.deployThreshold
        && x.draftCount === 0 && x.deployedCount === 0)
    if (unanswered.length > 0) {
      const reach = unanswered.reduce((n, x) => n + (x.engagements ?? 0), 0)
      out.push({
        id: 'social-unanswered',
        severity: 'critical',
        headline: `${unanswered.length} hostile ${unanswered.length === 1 ? 'thread is' : 'threads are'} above the line with nothing drafted.`,
        evidence: `${num(reach)} engagements across them; fastest `
          + `${unanswered.map((x) => x.velocity ?? 0).sort((a, b) => b - a)[0]?.toFixed(1) ?? '—'}/min.`,
        action: 'Ask the war room for drafts on the fastest one first. A reply approved after the audience has moved on costs the same and buys nothing.',
        votesAtStake: reach,
      })
    }
    if (g.totals.unscored > g.totals.threads * 0.5) {
      out.push({
        id: 'social-blind',
        severity: 'serious',
        headline: `${pct(g.totals.unscored / g.totals.threads)} of the social window has never been read.`,
        evidence: `${num(g.totals.unscored)} of ${num(g.totals.threads)} threads carry no threat score.`,
        action: 'Unread is not low-threat. Every social figure on this page is a floor rather than a measurement until that backlog is worked, so treat "nothing above the line" as unproven.',
        votesAtStake: 0,
      })
    }
  }

  // ── the desk ──────────────────────────────────────────────────────────────
  const inc = p.incidents
  if (inc && inc.openOlderThan48h > 0) {
    out.push({
      id: 'incidents-ageing',
      severity: 'serious',
      headline: `${num(inc.openOlderThan48h)} ${inc.openOlderThan48h === 1 ? 'incident has' : 'incidents have'} sat untouched past the ageing threshold.`,
      evidence: `${num(inc.open + inc.investigating)} open in total; `
        + `${num(inc.bySeverity.CRITICAL ?? 0)} of everything recorded is critical.`,
      action: 'Give each a named owner today. An incident nobody touched for two days reads as negligence in any review of how the campaign handled the period, whatever the outcome was.',
      votesAtStake: inc.openOlderThan48h,
    })
  }

  // ── money, which waits but does not improve ───────────────────────────────
  const o = p.programmes
  if (o) {
    const spend = Number(o.spendTotal ?? 0)
    const unattributed = Number(o.spendUnattributed ?? 0)
    if (spend > 0 && unattributed / spend > 0.1) {
      out.push({
        id: 'spend-unattributed',
        severity: 'serious',
        headline: `${pct(unattributed / spend)} of programme spend is not attached to anybody.`,
        evidence: `${o.currency ?? 'KES'} ${num(unattributed)} of ${num(spend)} recorded.`,
        action: 'This is the first thing an auditor opens with, and it does not decay — but it also does not improve on its own. Attach it or record why it cannot be attached before the campaign period closes.',
        votesAtStake: 0,
      })
    }
    if (o.unmatchableSubjects > o.distinctSubjects * 0.25) {
      out.push({
        id: 'headcount',
        severity: 'watch',
        headline: `The reach figure is a ceiling: ${num(o.unmatchableSubjects)} beneficiaries could not be de-duplicated.`,
        evidence: `${num(o.beneficiaryTotal)} enrolments resolve to ${num(o.distinctSubjects)} `
          + 'distinct people among those with a national ID recorded.',
        action: 'Never quote the enrolment sum in public — it counts a household in three programmes three times. Quote the de-duplicated figure and say it is a ceiling.',
        votesAtStake: o.distinctSubjects,
      })
    }
  }

  // ── whether anything said out loud would survive ──────────────────────────
  const a = p.assay
  if (a && a.standing.items > 0) {
    const solid = a.standing.atOrAboveFloor / a.standing.items
    if (solid < 0.5) {
      out.push({
        id: 'evidence-weak',
        severity: 'serious',
        headline: `Only ${pct(solid)} of the research file would survive being challenged.`,
        evidence: `${num(a.standing.atOrAboveFloor)} of ${num(a.standing.items)} items meet the `
          + `${a.floor.reliability}${a.floor.credibility} floor; ${num(a.standing.singleSource)} rest on one source.`,
        action: 'Do not build a public line on this file yet. One weak item quoted in a press conference discredits the strong ones beside it, and that is a bigger loss than staying quiet.',
        votesAtStake: a.standing.items - a.standing.atOrAboveFloor,
      })
    }
  }

  // ── the transfer pipeline ─────────────────────────────────────────────────
  const d = p.transfers
  if (d && d.successRate != null && d.successRate < 0.5 && d.raised > 0) {
    out.push({
      id: 'transfers-conversion',
      severity: 'serious',
      headline: `Under half of resolved transfer requests are landing (${pct(d.successRate)}).`,
      evidence: `${num(d.recorded)} recorded, ${num(d.rejected)} rejected, ${num(d.open)} still open.`,
      action: 'A rejected request costs the same as a recorded one and moves nobody. Read the rejection reasons before recruiting more intake — more volume through a leaking pipeline just makes the queue longer.',
      votesAtStake: d.rejected,
    })
  }

  // ── the two readings worth taking together ────────────────────────────────
  // Offered as a QUESTION, never as a causal claim. This page can see that two domains are
  // both under strain in the same period; it cannot see that one caused the other, and
  // saying so would send a campaign somewhere nothing here justifies.
  if (inc && f?.totals && f.day.pollsOpen && (inc.bySeverity.CRITICAL ?? 0) > 0
      && f.totals.cellsBehind > 0) {
    out.push({
      id: 'cross-read',
      severity: 'watch',
      headline: 'Worth reading together: critical incidents and cells behind pace fall in the same period.',
      evidence: `${num(inc.bySeverity.CRITICAL ?? 0)} critical incidents; `
        + `${num(f.totals.cellsBehind)} cells behind pace.`,
      action: 'Check whether the incidents and the slow cells are in the same places. This page cannot tell you they are related and it is not claiming they are — but if the map lines up, the response is a security one rather than a turnout one, and those are different teams.',
      votesAtStake: 0,
    })
  }

  const missing = cards.filter((c) => c.level === 'unknown')
  if (missing.length > 0) {
    out.push({
      id: 'unavailable',
      severity: 'watch',
      headline: `${missing.length} of ${cards.length} areas could not be read.`,
      evidence: missing.map((c) => c.label).join(', ') + '.',
      action: 'Treat these as unknown rather than clear. A rollup that did not return says nothing about what is happening there, and this board must not be read as complete while any of them is dark.',
      votesAtStake: 0,
      cellCodes: missing.map((c) => c.label),
    })
  }

  if (out.length === 0) {
    out.push({
      id: 'quiet',
      severity: 'good',
      headline: 'Nothing across the platform crosses a threshold right now.',
      evidence: cards.filter((c) => c.level !== 'unknown').map((c) => `${c.label} ${c.value}`).join(' · '),
      action: 'No action. This is the right moment to work on the things that never become urgent — evidence corroboration, baseline capture, the incident backlog.',
      votesAtStake: 0,
    })
  }

  /*
     Time pressure first, then size.

     A silent turnout cell at two in the afternoon outranks an unreconciled ledger — not
     because it is bigger, but because the ledger is still there tomorrow and the afternoon
     is not. `rank()` alone would sort by severity and then by stake, and stake is measured
     in different units per domain, so the perishable findings are pinned ahead of it.
  */
  const PERISHABLE = ['turnout-silent', 'turnout-pace', 'social-unanswered']
  const urgent = PERISHABLE.map((id) => out.find((f2) => f2.id === id)).filter(Boolean) as Finding[]
  const rest = rank(out.filter((f2) => !PERISHABLE.includes(f2.id)))
  return [...urgent, ...rest]
}

const ROUTES: { id: string; words: string[] }[] = [
  { id: 'turnout-pace', words: ['turnout', 'votes', 'behind', 'pace', 'polling day', 'clock', 'vans'] },
  { id: 'turnout-silent', words: ['silent', 'quiet', 'cells', 'coordinator', 'no report'] },
  { id: 'social-unanswered', words: ['social', 'thread', 'attack', 'respond', 'reply', 'online', 'twitter'] },
  { id: 'social-blind', words: ['unread', 'unscored', 'blind', 'coverage of social'] },
  { id: 'incidents-ageing', words: ['incident', 'desk', 'backlog', 'ageing', 'aging', 'open case'] },
  { id: 'spend-unattributed', words: ['spend', 'money', 'audit', 'unattributed', 'ledger', 'account'] },
  { id: 'headcount', words: ['how many people', 'reach', 'beneficiar', 'headcount', 'quote', 'dedup'] },
  { id: 'evidence-weak', words: ['evidence', 'dossier', 'file', 'press conference', 'stand behind', 'prove'] },
  { id: 'transfers-conversion', words: ['transfer', 'register', 'moving voters', 'relocat'] },
  { id: 'cross-read', words: ['related', 'connect', 'pattern', 'together', 'correlat'] },
  { id: 'unavailable', words: ['missing', 'unavailable', 'dark', 'not loading', 'complete'] },
  { id: 'quiet', words: ['anything', 'all clear', 'quiet', 'ok', 'fine', 'today'] },
]

export function ask(question: string, findings: Finding[]) {
  return route(question, findings, ROUTES)
}

/**
 * The platform, written out for the model.
 *
 * Every module's own refusals are repeated here, because the model sees these numbers with
 * none of the context each module's own council supplies — and the failure modes are the
 * same ones: unread treated as safe, unverified treated as false, an enrolment sum treated
 * as a headcount, a correlation treated as a cause.
 */
export function platformContext(p: Platform, cards: DomainCard[], findings: Finding[]): string {
  const f = p.floor
  const g = p.graph
  const inc = p.incidents
  const o = p.programmes
  const a = p.assay
  const d = p.transfers

  return [
    'You are the strategic adviser to a Kenyan political campaign, reading its whole platform',
    'at once. Answer in plain language for a campaign manager. Be decisive: say what to do',
    'first and why, and name what you would leave until tomorrow.',
    '',
    'Five rules you must not break:',
    '(1) An UNREAD social thread or an UNVERIFIED incident means nobody has checked. It does',
    'NOT mean harmless or false.',
    '(2) Programme ENROLMENTS are not people. Use the de-duplicated headcount, and treat it as',
    'a ceiling where beneficiaries could not be matched.',
    '(3) Do NOT assert that one domain caused another. You may say two readings are worth',
    'taking together and what would confirm it, but never state a cross-domain cause.',
    '(4) The IEBC performs transfers and runs the poll; this platform records. Never promise',
    'an outcome the campaign does not control.',
    '(5) Never accuse anybody. Flags and coordination signals are reasons to look.',
    '',
    'STATE OF EACH AREA:',
    ...cards.map((c) => `- ${c.label} [${c.level}]: ${c.value} ${c.unit}. ${c.headline}`
      + (c.facts.length ? ` (${c.facts.map((x) => `${x.label} ${x.value}`).join(', ')})` : '')),
    '',
    f?.totals && f.day.pollsOpen
      ? `POLLING DAY IS LIVE: ${f.totals.voted} of ${f.totals.members} members voted, `
        + `${f.totals.outstanding} outstanding, ${f.day.minutesToClose} minutes to close, `
        + `${f.totals.cellsBehind} cells behind pace, ${f.totals.cellsStalled} silent, `
        + `${f.totals.transportGaps} unmatched lifts, ${f.totals.escalationsOpen} open escalations.`
      : f?.totals
        ? `POLLS ARE CLOSED. Final: ${f.totals.voted} of ${f.totals.members} members voted.`
        : 'TURNOUT: no election is in its polling window.',
    g ? `SOCIAL: ${g.totals.threads} threads, ${g.totals.engagements} engagements, `
      + `${g.totals.aboveThreshold} above threat ${g.deployThreshold}, `
      + `${g.totals.unscored} NEVER READ, ${g.totals.draftsAwaiting} drafts awaiting a human, `
      + `${g.totals.coordinated} narratives with repeated phrasing.` : '',
    inc ? `INCIDENTS: ${inc.total} recorded, ${inc.open + inc.investigating} open, `
      + `${inc.openOlderThan48h} past the ageing threshold, ${inc.bySeverity.CRITICAL ?? 0} critical, `
      + `${inc.totalDeaths} deaths, ${inc.totalInjuries} injuries, verified share `
      + `${inc.verifiedShare == null ? 'n/a' : Math.round(inc.verifiedShare * 100) + '%'}.` : '',
    d ? `TRANSFERS: ${d.raised} raised, ${d.recorded} recorded, ${d.rejected} rejected, `
      + `${d.open} open. Success ${d.successRate == null ? 'n/a' : Math.round(d.successRate * 100) + '%'}.` : '',
    o ? `PROGRAMMES: ${o.programmes} programmes, enrolments ${o.beneficiaryTotal} resolving to `
      + `${o.distinctSubjects} DISTINCT PEOPLE, ${o.unmatchableSubjects} unmatchable. `
      + `Budget ${o.budgetTotal ?? 'n/a'}, spend ${o.spendTotal ?? 'n/a'}, unattributed `
      + `${o.spendUnattributed ?? 'n/a'} ${o.currency ?? 'KES'}.` : '',
    a ? `EVIDENCE: ${a.standing.items} items, ${a.standing.atOrAboveFloor} meet the `
      + `${a.floor.reliability}${a.floor.credibility} quality floor, ${a.standing.singleSource} rest on `
      + `one source, ${a.standing.disputed} disputed, ${a.standing.debunked} debunked.` : '',
    p.polls ? `POLLING STUDIO: ${p.polls.length} polls, `
      + `${p.polls.filter((x) => x.statusCode === 'LIVE').length} in the field.` : '',
    '',
    'WHAT OUR OWN RULES ALREADY FLAGGED (agree, refine, disagree, or re-order):',
    ...findings.map((x) => `- [${x.severity}] ${x.headline} ${x.evidence}`),
  ].filter(Boolean).join('\n')
}
